/**
 * Salesforce Integration Service
 * Makes authenticated calls to the TriServe Auth API Salesforce endpoints.
 * Authentication: Bearer JWT + X-TriServe-App-Key header (server handles SFDC OAuth internally).
 */

import { API_CONFIG, DEBUG_CONFIG } from '../config';
import { getJwt } from './authService';
import { SALESFORCE_ROUTES, getTriServeApiBaseUrl, joinApiUrl } from '../utils/apiRoutes';

const getJwtFromStorageIfPresent = () => getJwt();

/**
 * Builds common headers for all TriServe Auth API calls.
 * Both Bearer token and X-TriServe-App-Key are required by the global security scheme.
 */
const buildTriServeHeaders = (jwtToken, extraHeaders = {}) => {
  const headers = {
    'X-TriServe-App-Key': API_CONFIG.AUTH_APP_KEY || 'triserve-energy',
    'Accept': 'application/json',
    ...extraHeaders
  };

  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  return headers;
};

/**
 * Performs an authenticated fetch to the TriServe Auth API.
 * Sends JWT Authorization header + X-TriServe-App-Key on every request.
 */
const triserveAuthedFetch = async (url, init = {}, options = {}) => {
  const jwtToken = options.jwtToken ?? getJwtFromStorageIfPresent();

  if (!jwtToken) {
    throw new Error('Not authenticated: missing TriServe JWT token');
  }

  const headers = new Headers(buildTriServeHeaders(jwtToken, init.headers || {}));

  return fetch(url, {
    ...init,
    credentials: 'include',
    headers
  });
};

const normalizeAccountsResponse = (json) => {
  const payload = json?.data ?? json ?? {};
  const accounts = Array.isArray(payload?.accounts) ? payload.accounts : [];

  return {
    succeeded: json?.succeeded ?? payload?.isSuccess ?? true,
    messages: Array.isArray(json?.messages) ? json.messages : [],
    totalCount: typeof payload?.totalCount === 'number' ? payload.totalCount : accounts.length,
    accounts,
    error: payload?.error ?? null
  };
};

const normalizeAccountResponse = (json) => {
  const payload = json?.data ?? json ?? {};

  return {
    succeeded: json?.succeeded ?? true,
    messages: Array.isArray(json?.messages) ? json.messages : [],
    account: payload,
    error: typeof payload?.error === 'string' ? payload.error : null
  };
};

const normalizeIndustryValue = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

/**
 * Fetches Salesforce accounts through the TriServe Auth API.
 * Supports optional server-side industry filtering via the by-industry endpoint,
 * with client-side fallback when fetching all accounts.
 */
export const fetchSalesforceAccounts = async (
  { limit = 2000, offset = 0, industry, signal } = {},
  options = {}
) => {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset)
  });

  const response = await triserveAuthedFetch(`${SALESFORCE_ROUTES.accounts()}?${params.toString()}`, {
    method: 'GET',
    signal
  }, options);

  const text = await response.text();
  let json;

  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok) {
    const detail = text || response.statusText || 'Request failed';
    throw new Error(`Unable to load Salesforce accounts: ${response.status} ${response.statusText} - ${detail}`);
  }

  const result = normalizeAccountsResponse(json);

  if (result.succeeded === false || result.error) {
    const detail = result.error || result.messages.join(' | ') || 'Salesforce accounts request failed';
    throw new Error(detail);
  }

  const filteredAccounts = industry
    ? result.accounts.filter((account) => normalizeIndustryValue(account?.industry) === normalizeIndustryValue(industry))
    : result.accounts;

  return {
    success: true,
    totalCount: filteredAccounts.length,
    rawTotalCount: result.totalCount,
    accounts: filteredAccounts
  };
};

export const fetchSalesforceAccountById = async (id, { signal } = {}, options = {}) => {
  if (!id) {
    throw new Error('A Salesforce account ID is required.');
  }

  const response = await triserveAuthedFetch(SALESFORCE_ROUTES.accountById(id), {
    method: 'GET',
    signal
  }, options);

  const text = await response.text();
  let json;

  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok) {
    const detail = text || response.statusText || 'Request failed';
    throw new Error(`Unable to load Salesforce account: ${response.status} ${response.statusText} - ${detail}`);
  }

  const result = normalizeAccountResponse(json);

  if (result.succeeded === false || result.error) {
    const detail = result.error || result.messages.join(' | ') || 'Salesforce account request failed';
    throw new Error(detail);
  }

  return {
    success: true,
    account: result.account
  };
};

/**
 * Creates a composite Account + Contact + Opportunity in Salesforce via the TriServe Auth API.
 * Uses the Composite Tree endpoint for a single-transaction create.
 * @param {Object} customerData - Customer form data
 * @param {string} accountNumber - Generated TES account number
 * @returns {Promise<Object>} { success, message, accountId, data }
 */
export const createSalesforceCustomer = async (customerData, accountNumber, options = {}) => {
  try {
    if (DEBUG_CONFIG.LOG_API_CALLS) {
      console.log('Creating SFDC composite record for:', { accountNumber, customerData });
    }

    const payload = buildSalesforcePayload(customerData, accountNumber);

    if (DEBUG_CONFIG.LOG_API_CALLS) {
      console.log('SFDC Payload:', JSON.stringify(payload, null, 2));
    }

    const response = await triserveAuthedFetch(SALESFORCE_ROUTES.accounts(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }, options);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`SFDC API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const result = await response.json();

    if (DEBUG_CONFIG.LOG_API_CALLS) {
      console.log('SFDC API Response:', result);
    }

    // TriServeAuth.WebApi returns { succeeded, messages, data: { sfdcAccountId, ... } }
    if (result && result.succeeded === false) {
      const message = Array.isArray(result.messages) ? result.messages.join(' | ') : 'SFDC create failed';
      throw new Error(message);
    }

    const accountId = result?.data?.sfdcAccountId ?? null;

    return {
      success: true,
      message: 'Customer record created successfully in Salesforce',
      accountId,
      data: result
    };

  } catch (error) {
    console.error('Salesforce integration error:', error);
    throw error;
  }
};

export const createSalesforceCase = async (caseData, options = {}) => {
  const response = await triserveAuthedFetch(joinApiUrl(getTriServeApiBaseUrl(), '/api/v1/salesforce/cases'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(caseData)
  }, options);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`SF Case create failed: ${response.status} - ${errorText}`);
  }

  return response.json();
};

export const updateSalesforceCase = async (caseId, updates, options = {}) => {
  const id = caseId || 'lookup';
  const response = await triserveAuthedFetch(joinApiUrl(getTriServeApiBaseUrl(), `/api/v1/salesforce/cases/${encodeURIComponent(id)}`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  }, options);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`SF Case update failed: ${response.status} - ${errorText}`);
  }

  return response.json();
};

export const updateSalesforceAccountAddress = async (accountNumber, address, options = {}) => {
  const response = await triserveAuthedFetch(joinApiUrl(getTriServeApiBaseUrl(), '/api/v1/salesforce/accounts/address'), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accountNumber, ...address })
  }, options);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`SF Account address update failed: ${response.status} - ${errorText}`);
  }

  return response.json();
};

/**
 * Builds the Salesforce composite payload from customer data.
 * Shape matches CreateSfdcCompositeAccountRequest.
 */
const buildSalesforcePayload = (customerData, accountNumber) => {
  const serviceStartDate = customerData.serviceStartDate || new Date().toISOString().split('T')[0];

  const baseSetupFee = 150.00;
  const depositAmount = customerData.creditResults?.deposit || 0;
  const totalAmount = baseSetupFee + depositAmount;

  const accountType = customerData.serviceType === 'commercial' ? 'Commercial' : 'Residential';
  const industry = 'Utilities';

  const cleanPhone = customerData.phone.replace(/[^\d]/g, '');
  const formattedPhone = `+1-${cleanPhone.slice(0, 3)}-${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6, 10)}`;

  return {
    name: `${customerData.firstName} ${customerData.lastName}`,
    accountNumber,
    phone: formattedPhone,
    type: accountType,
    industry,
    billingStreet: customerData.streetAddress,
    billingCity: customerData.city,
    billingState: customerData.state,
    billingPostalCode: customerData.zipCode,
    contact: {
      firstName: customerData.firstName,
      lastName: customerData.lastName,
      email: customerData.email,
      phone: formattedPhone
    },
    opportunity: {
      name: `New ${accountType} Utility Setup - ${customerData.firstName} ${customerData.lastName}`,
      closeDate: serviceStartDate,
      stageName: 'Closed Won',
      probability: '100',
      amount: totalAmount.toFixed(2),
      type: 'New Service',
      leadSource: 'CSR Portal'
    }
  };
};

/**
 * Returns true if the TriServe Auth API (and thus SFDC integration) is configured.
 */
export const isSalesforceAvailable = () => getTriServeApiBaseUrl() !== '';
